import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import type { A11yStatus } from "@metrickle/schema";
import { api, useApiMutation } from "../lib/api";
import { useCan } from "../lib/access";
import { fmtAgo, fmtNumber } from "../lib/format";
import { track } from "../lib/track";
import { Ago, Button, CardSection, CodeBlock, Empty, Modal, Table, TableScroll, notifyError, toaster, useConfirm, Skeleton } from "./ui";

/**
 * Integrations page: accessibility scans posted from CI. The latest scan of each page is kept and
 * shown next to the friction on it, so a WCAG failure sits beside the people it's stopping.
 */

export const useA11yStatus = (appId: string) => useQuery({ queryKey: ["a11y", appId], queryFn: () => api<A11yStatus>(`/apps/${appId}/a11y`) });

const SETUP = (url: string) => `# 1. axe CLI against your staging or preview URLs, after deploy:
npx @axe-core/cli https://staging.example.com/ https://staging.example.com/checkout \\
  --save axe-results.json --dir .
curl -X POST "${url}?version=$GIT_SHA&environment=staging" \\
  -H "content-type: application/json" --data-binary @axe-results.json

# 2. Or from Playwright tests (@axe-core/playwright), once per page:
#    const results = await new AxeBuilder({ page }).analyze();
#    await fetch(process.env.METRICKLE_SCAN_HOOK, { method: "POST",
#      headers: { "content-type": "application/json" }, body: JSON.stringify(results) });

# 3. Or Lighthouse reports (only the accessibility category is read):
npx lighthouse https://staging.example.com/checkout --only-categories=accessibility \\
  --output json --output-path lighthouse.json --chrome-flags="--headless"
curl -X POST "${url}?version=$GIT_SHA" \\
  -H "content-type: application/json" --data-binary @lighthouse.json

# Each page keeps only its latest scan: a clean run clears it. Up to 10 MB per request.`;

export function A11yScansSection({ appId }: { appId: string }) {
  const can = useCan(appId);
  const status = useA11yStatus(appId);
  const [dialog, setDialog] = useState<"hook" | "pages" | null>(null);
  const close = () => setDialog(null);
  const s = status.data;
  const pagesWith = s?.pages.filter((p) => p.violations > 0).length ?? 0;

  return (
    <CardSection flush>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <dl className="grid min-w-0 grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
          <dt className="text-ink-3">Scan hook</dt>
          <dd className={s?.configured ? "text-ink" : "text-ink-2"}>
            {!s?.configured ? "Not set up" : s.lastScan ? `Last scan ${fmtAgo(s.lastScan.at)}${s.lastScan.tool ? ` · ${s.lastScan.tool}` : ""}` : "Set up, no scans yet"}
          </dd>
          <dt className="text-ink-3">Visitors' browsers</dt>
          <dd className={s?.sdkEnabled ? "text-ink" : "text-ink-2"}>
            {s?.sdkEnabled ? "Checking pages CI doesn't scan" : "Off"}
            {" · "}
            <Link className="text-accent-text hover:underline" to={`/apps/${appId}/settings`}>
              {s?.sdkEnabled ? "Change" : "Turn on"}
              <span className="sr-only"> accessibility scans in visitors' browsers, in research settings</span>
            </Link>
          </dd>
          <dt className="text-ink-3">Pages scanned</dt>
          <dd className="text-ink">
            {fmtNumber(s?.pages.length ?? 0)}
            {s?.pages.length ? (
              <>
                {" · "}
                {pagesWith ? `${fmtNumber(pagesWith)} with violations` : "all clean"}
                {" · "}
                <Button variant="link" onClick={() => setDialog("pages")}>
                  View pages
                </Button>
              </>
            ) : null}
          </dd>
        </dl>
        {can("apps.keys") && (
          <Button size="sm" onClick={() => setDialog("hook")}>
            {s?.configured ? "Scan hook" : "Set up scan hook"}
          </Button>
        )}
      </div>
      <Modal
        open={dialog === "hook"}
        onOpenChange={(o) => !o && close()}
        title="Accessibility scan hook"
        description="Post axe-core results or Lighthouse reports from CI. Anyone with this URL can replace this app's scan results, so keep it in your CI secrets."
        wide
      >
        {dialog === "hook" && <HookSetup appId={appId} />}
      </Modal>
      <Modal open={dialog === "pages"} onOpenChange={(o) => !o && close()} title="Scanned pages" description="Each page's latest scan, worst first. Violations on elements people struggle with also show on the Friction page." wide>
        {dialog === "pages" && s && <ScannedPages appId={appId} status={s} />}
      </Modal>
    </CardSection>
  );
}

function HookSetup({ appId }: { appId: string }) {
  const hook = useQuery({ queryKey: ["a11y-hook", appId], queryFn: () => api<{ url: string }>(`/apps/${appId}/a11y-hook`) });
  const rotate = useApiMutation(() => api<{ url: string }>(`/apps/${appId}/a11y-hook/rotate`, { method: "POST" }), [["a11y-hook", appId], ["a11y", appId]]);
  const { confirm, dialog } = useConfirm();
  if (!hook.data)
    return (
      <div aria-busy="true">
        <Skeleton className="h-24" />
        <span className="sr-only">Loading…</span>
      </div>
    );
  return (
    <div className="space-y-3">
      <CodeBlock code={SETUP(hook.data.url)} label="shell" language="shell" onCopy={() => track("a11y_hook_copied")} />
      <Button
        size="sm"
        variant="ghost"
        pending={rotate.isPending}
        pendingLabel="Replacing…"
        onClick={async () => {
          if (await confirm({ title: "Replace the scan hook URL?", description: "The current URL stops working straight away. Update it in your CI secrets.", confirmLabel: "Replace URL" }))
            rotate.mutate(undefined, { onSuccess: () => toaster.success({ title: "New scan hook URL", description: "Update your CI secrets." }), onError: notifyError });
        }}
      >
        <RefreshCw size={13} aria-hidden /> Replace URL
      </Button>
      {dialog}
    </div>
  );
}

function ScannedPages({ appId, status }: { appId: string; status: A11yStatus }) {
  if (!status.pages.length) return <Empty title="No pages scanned yet">Post axe-core results to the scan hook from CI, or turn on scans in visitors' browsers in research settings.</Empty>;
  return (
    <TableScroll label="Scanned pages">
      <Table caption="Scanned pages with their latest violations, worst first">
        <thead>
          <tr>
            <th scope="col">Page</th>
            <th scope="col" className="text-right">Critical</th>
            <th scope="col" className="text-right">Serious</th>
            <th scope="col" className="text-right">All</th>
            <th scope="col">Scanned</th>
            <th scope="col">Source</th>
          </tr>
        </thead>
        <tbody>
          {status.pages.map((p) => (
            <tr key={p.path}>
              <td className="max-w-64 truncate font-mono text-[13px]">
                <Link className="text-accent-text hover:underline" to={`/apps/${appId}/friction?filters=${encodeURIComponent(JSON.stringify([{ field: "path", op: "eq", value: p.path }]))}`}>
                  {p.path}
                  <span className="sr-only"> (friction on this page)</span>
                </Link>
              </td>
              <td className={p.critical ? "text-right font-medium text-bad" : "text-right text-ink-3"}>{fmtNumber(p.critical)}</td>
              <td className={p.serious ? "text-right font-medium text-bad" : "text-right text-ink-3"}>{fmtNumber(p.serious)}</td>
              <td className="text-right text-ink">{fmtNumber(p.violations)}</td>
              <td className="whitespace-nowrap text-xs text-ink-3">
                <Ago t={p.scannedAt} />
              </td>
              <td className="whitespace-nowrap text-xs text-ink-2">{p.source === "sdk" ? "Visitors' browsers" : "CI"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
}
